import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminGetFinancialDashboard } from '../../lib/api';
import { Sparkles, RefreshCw, DollarSign, TrendingUp, TrendingDown, Wallet, Package, Boxes, Truck, Send, PiggyBank, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { adminGetAiInsights, adminGenerateAiInsights, adminGetOwnerPayAdvice, adminGenerateOwnerPayAdvice, adminGetOwnerSchedule, adminStartOwnerSchedule, adminStopOwnerSchedule } from '../../lib/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
const fmt2 = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function useCountUp(target, duration = 1100) {
  const [val, setVal] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const from = 0, start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setVal(from + (target - from) * (1 - Math.pow(1 - p, 3)));
      if (p < 1) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [target, duration]);
  return val;
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 120, damping: 18 } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };

function Kpi({ testid, icon: Icon, label, value, positive, sub }) {
  const v = useCountUp(Math.abs(value));
  const negative = value < 0;
  return (
    <motion.div variants={fadeUp} data-testid={testid}
      className="relative col-span-1 md:col-span-3 rounded-2xl border border-alp-gold/20 bg-alp-card/40 backdrop-blur-2xl p-6 overflow-hidden hover:border-alp-gold/60 hover:-translate-y-1 transition-[border-color,transform] duration-300 shadow-[0_0_30px_rgba(212,175,55,0.08)]">
      <div className="absolute inset-0 bg-gradient-to-br from-alp-gold/[0.06] to-transparent pointer-events-none" />
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">
        <Icon size={14} strokeWidth={1.5} /> {label}
      </div>
      <div className={`font-serif text-3xl lg:text-4xl ${negative ? 'text-red-400' : positive ? 'text-emerald-300' : 'text-alp-cream'}`}>
        {negative ? '-' : ''}{fmt(v)}
      </div>
      {sub && <div className="text-alp-cream/50 text-xs mt-2">{sub}</div>}
    </motion.div>
  );
}

function GlassTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-alp-gold/30 bg-alp-black/90 backdrop-blur-xl px-4 py-3 text-xs">
      <div className="text-alp-gold tracking-[0.15em] uppercase mb-1.5">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex justify-between gap-6 text-alp-cream py-0.5">
          <span className="capitalize text-alp-cream/60">{p.dataKey}</span>
          <span>{fmt2(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function FinancialDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(60);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [pay, setPay] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [sched, setSched] = useState(null);
  const [schedBusy, setSchedBusy] = useState(false);
  useEffect(() => { adminGetAiInsights().then((r) => { if (r.insights) setAi(r); }).catch(() => {}); }, []);
  useEffect(() => { adminGetOwnerPayAdvice().then((r) => { if (r.advice) setPay(r.advice); }).catch(() => {}); }, []);
  useEffect(() => { adminGetOwnerSchedule().then(setSched).catch(() => {}); }, []);
  const genPay = async () => {
    setPayLoading(true);
    try { const r = await adminGenerateOwnerPayAdvice(); setPay(r.advice); } catch (e) {} finally { setPayLoading(false); }
  };
  const startSched = async () => {
    setSchedBusy(true);
    try {
      const r = await adminStartOwnerSchedule(pay?.weeklyWage);
      setSched({ active: true, weeklyGross: r.weeklyGross, nextPeriodEnd: r.nextPeriodEnd });
      toast.success(`Pay run ${r.firstRun?.runNumber || ''} created — weekly auto-pay is on`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start auto-pay.'); }
    finally { setSchedBusy(false); }
  };
  const stopSched = async () => {
    try { await adminStopOwnerSchedule(); setSched({ active: false }); toast('Weekly auto-pay stopped.'); } catch (e) {}
  };
  const regenAi = async () => {
    setAiLoading(true);
    try { setAi(await adminGenerateAiInsights()); } catch (e) {} finally { setAiLoading(false); }
  };

  const load = useCallback(async (d = days) => {
    setLoading(true);
    try { setData(await adminGetFinancialDashboard(d)); } catch (e) {} finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(days); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) return <p className="text-alp-gray py-16 text-center" data-testid="findash-loading">Crunching your numbers...</p>;
  if (!data) return null;
  const { money, series, topProducts, forecast, pipeline, insights } = data;
  const maxShare = topProducts[0]?.share || 1;
  const combined = (forecast.onHandRevenue || 0) + (forecast.incomingRevenue || 0) || 1;

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-6" data-testid="financial-dashboard">
      <motion.div variants={fadeUp} className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-alp-cream">Financial Dashboard</h2>
          <p className="text-alp-cream/50 text-sm mt-1">Live from paid orders, product costs and stock &mdash; not purchase entries.</p>
        </div>
        <div className="flex items-center gap-2">
          {[30, 60, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`findash-range-${d}`}
              className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.12em] uppercase border transition-colors ${days === d ? 'bg-alp-gold text-alp-black border-alp-gold' : 'text-alp-cream/70 border-alp-line hover:border-alp-gold/60'}`}>{d}d</button>
          ))}
          <button onClick={() => load(days)} data-testid="findash-refresh" className="p-2 rounded-full border border-alp-line text-alp-cream/70 hover:border-alp-gold hover:text-alp-gold transition-colors"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
        </div>
      </motion.div>

      {/* AI insights — tracing beam */}
      <motion.div variants={fadeUp} className="alp-beam rounded-3xl p-[1.5px]" data-testid="findash-insights">
        <div className="rounded-[calc(1.5rem-1.5px)] bg-alp-black/95 backdrop-blur-2xl px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={16} strokeWidth={1.5} className="text-alp-gold animate-pulse" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-alp-gold">Luxe Intelligence</span>
              {ai && <span className="text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full border border-alp-gold/40 text-alp-gold/90" data-testid="ai-source-badge">Claude</span>}
            </div>
            <button onClick={regenAi} disabled={aiLoading} data-testid="findash-ai-regenerate"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
              <Sparkles size={11} className={aiLoading ? 'animate-spin' : ''} /> {aiLoading ? 'Claude is thinking...' : ai ? 'Refresh with Claude' : 'Ask Claude'}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {(ai?.insights || insights).map((t, i) => (
              <motion.p key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 + i * 0.18 }}
                className="text-alp-cream/85 text-sm leading-relaxed flex gap-2" data-testid={`ai-insight-${i}`}>
                <span className="text-alp-gold shrink-0 mt-[1px]">&#9670;</span>{t}
              </motion.p>
            ))}
          </div>
        </div>
      </motion.div>

      {/* KPI bento row */}
      <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-5">
        <Kpi testid="kpi-money-in" icon={DollarSign} label="Money In" value={money.moneyIn} positive sub="All paid orders, ever" />
        <Kpi testid="kpi-money-spent" icon={TrendingDown} label="Money Spent" value={money.moneySpent} sub="Stock purchases + expenses" />
        <Kpi testid="kpi-sold-profit" icon={TrendingUp} label="Profit on Sold" value={money.soldProfit} positive={money.soldProfit >= 0} sub="Sales minus cost of units sold" />
        <Kpi testid="kpi-cash-position" icon={Wallet} label="Cash Position" value={money.cashPosition} positive={money.cashPosition >= 0} sub="Money in minus ALL spend" />
      </motion.div>

      {/* Pay Yourself Safely */}
      <motion.div variants={fadeUp} className="rounded-3xl border border-alp-gold/20 bg-alp-card/40 backdrop-blur-2xl p-6 relative overflow-hidden" data-testid="findash-owner-pay">
        <div className="absolute -top-20 right-10 w-56 h-56 rounded-full bg-alp-gold/10 blur-3xl pointer-events-none" />
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <PiggyBank size={16} strokeWidth={1.5} className="text-alp-gold" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-alp-gold">Pay Yourself Safely</span>
          </div>
          <div className="flex items-center gap-2">
            {pay && sched && !sched.active && (
              <button onClick={startSched} disabled={schedBusy} data-testid="owner-pay-start-schedule"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/15 transition-colors disabled:opacity-50">
                <CalendarClock size={11} /> {schedBusy ? 'Setting up...' : `Auto-pay ${fmt(pay.weeklyWage)}/wk`}
              </button>
            )}
            <button onClick={genPay} disabled={payLoading} data-testid="owner-pay-generate"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
              <Sparkles size={11} className={payLoading ? 'animate-spin' : ''} /> {payLoading ? 'Crunching your figures...' : pay ? 'Refresh advice' : 'Work out my wage'}
            </button>
          </div>
        </div>
        {sched && sched.active && (
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3" data-testid="owner-pay-schedule-active">
            <p className="text-emerald-300 text-sm leading-relaxed">
              <CalendarClock size={13} className="inline mr-1.5 -mt-[2px]" />
              Weekly auto-pay is on &mdash; {fmt(sched.weeklyGross)} gross with PAYG withheld + 12% super. A draft pay run lands in Payroll each week (next: {sched.nextPeriodEnd}); mark it paid when you transfer the money.
            </p>
            <button onClick={stopSched} data-testid="owner-pay-stop-schedule"
              className="text-[10px] uppercase tracking-[0.15em] text-alp-cream/60 hover:text-red-300 border border-alp-cream/20 hover:border-red-300/40 rounded-full px-3 py-1.5 transition-colors">Stop</button>
          </div>
        )}
        {!pay && <p className="text-alp-cream/55 text-sm">Get a recommended owner wage and security tips worked out from your live profit, GST owing and cash position.</p>}
        {pay && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <div className="font-serif text-4xl text-emerald-300" data-testid="owner-pay-weekly">{fmt(pay.weeklyWage)}<span className="text-base text-alp-cream/50"> / week gross</span></div>
              <div className="text-alp-cream/60 text-sm mt-1">{fmt(pay.monthlyWage)} / month recommended</div>
              {pay.weeklySuper > 0 && (
                <div className="text-alp-gold/80 text-xs mt-1.5" data-testid="owner-pay-super">+ {fmt(pay.weeklySuper)}/wk super on top (12%, paid by the business)</div>
              )}
              <p className="text-alp-cream/60 text-xs leading-relaxed mt-3" data-testid="owner-pay-rationale">{pay.rationale}</p>
            </div>
            <div className="md:col-span-8 space-y-2">
              {(pay.tips || []).map((t, i) => (
                <p key={i} className="text-alp-cream/85 text-sm leading-relaxed flex gap-2" data-testid={`owner-pay-tip-${i}`}>
                  <span className="text-alp-gold shrink-0 mt-[1px]">&#9670;</span>{t}
                </p>
              ))}
            </div>
          </div>
        )}
        <p className="text-[10px] text-alp-cream/35 mt-4">General guidance worked out from your store data &mdash; not formal financial advice.</p>
      </motion.div>

      {/* Chart + top products */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        <motion.div variants={fadeUp} className="md:col-span-8 rounded-3xl border border-alp-gold/20 bg-alp-card/40 backdrop-blur-2xl p-6 relative overflow-hidden" data-testid="findash-chart">
          <div className="absolute -top-24 left-1/3 w-72 h-72 rounded-full bg-alp-gold/10 blur-3xl pointer-events-none" />
          <div className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-5">Revenue &amp; Profit &mdash; last {days} days</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(212,175,55,0.4)" />
                    <stop offset="100%" stopColor="rgba(212,175,55,0)" />
                  </linearGradient>
                  <linearGradient id="creamFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(16,185,129,0.28)" />
                    <stop offset="100%" stopColor="rgba(16,185,129,0)" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: 'rgba(240,235,224,0.4)', fontSize: 10 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} minTickGap={28} />
                <YAxis tick={{ fill: 'rgba(240,235,224,0.4)', fontSize: 10 }} tickFormatter={(v) => `$${v}`} axisLine={false} tickLine={false} />
                <Tooltip content={<GlassTooltip />} />
                <Area type="monotone" dataKey="revenue" stroke="#d4af37" strokeWidth={2} fill="url(#goldFill)" />
                <Area type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={1.5} fill="url(#creamFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-5 mt-3 text-[11px] text-alp-cream/60">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-alp-gold inline-block" /> Revenue</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Profit</span>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="md:col-span-4 rounded-3xl border border-alp-gold/20 bg-alp-card/40 backdrop-blur-2xl p-6" data-testid="findash-top-products">
          <div className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-5">Top Products (all-time)</div>
          <div className="space-y-4">
            {topProducts.length === 0 && <p className="text-alp-cream/50 text-sm">No paid sales yet.</p>}
            {topProducts.map((p, i) => (
              <div key={i} data-testid={`findash-top-${i}`}>
                <div className="flex justify-between items-baseline gap-2 text-sm">
                  <span className="text-alp-cream truncate">{p.name} <span className="text-alp-cream/40 text-xs">{p.size}</span></span>
                  <span className="text-alp-gold shrink-0">{fmt(p.revenue)}</span>
                </div>
                <div className="flex justify-between text-[11px] text-alp-cream/45 mb-1.5">
                  <span>{p.units} units</span><span className="text-emerald-400/80">{fmt(p.profit)} profit</span>
                </div>
                <div className="h-1.5 rounded-full bg-alp-line/40 overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${(p.share / maxShare) * 100}%` }} transition={{ delay: 0.4 + i * 0.1, duration: 0.7 }}
                    className="h-full rounded-full bg-gradient-to-r from-alp-gold/70 to-alp-gold shadow-[0_0_8px_rgba(212,175,55,0.5)]" />
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Forecast + pipeline */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        <motion.div variants={fadeUp} className="md:col-span-6 rounded-3xl border border-alp-gold/20 bg-alp-card/40 backdrop-blur-2xl p-6" data-testid="findash-forecast">
          <div className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-4">Locked-Up Potential</div>
          <div className="font-serif text-3xl text-alp-cream mb-1">{fmt(forecast.combinedRevenue)}</div>
          <p className="text-alp-cream/50 text-xs mb-4">revenue sitting in stock on hand + incoming ({fmt(forecast.combinedProfit)} profit)</p>
          <div className="h-2.5 rounded-full bg-alp-line/40 overflow-hidden flex">
            <motion.div initial={{ width: 0 }} animate={{ width: `${(forecast.onHandRevenue / combined) * 100}%` }} transition={{ delay: 0.5, duration: 0.8 }} className="h-full bg-alp-gold" />
            <motion.div initial={{ width: 0 }} animate={{ width: `${(forecast.incomingRevenue / combined) * 100}%` }} transition={{ delay: 0.7, duration: 0.8 }} className="h-full bg-alp-gold/40" />
          </div>
          <div className="flex gap-5 mt-3 text-[11px] text-alp-cream/60">
            <span className="inline-flex items-center gap-1.5"><Boxes size={12} className="text-alp-gold" /> On hand {fmt(forecast.onHandRevenue)}</span>
            <span className="inline-flex items-center gap-1.5"><Truck size={12} className="text-alp-gold/60" /> Incoming {fmt(forecast.incomingRevenue)}</span>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="md:col-span-6 rounded-3xl border border-alp-gold/20 bg-alp-card/40 backdrop-blur-2xl p-6" data-testid="findash-pipeline">
          <div className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-4">Pipeline Pulse</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: Package, label: 'To pack', v: pipeline.toPack, sub: fmt(pipeline.toPackValue), tid: 'pulse-topack' },
              { icon: DollarSign, label: 'Awaiting pay', v: pipeline.newOrders, sub: 'new orders', tid: 'pulse-new' },
              { icon: Send, label: 'To export', v: pipeline.readyToExport, sub: 'AusPost CSV', tid: 'pulse-export' },
              { icon: Truck, label: 'Shipped 7d', v: pipeline.shipped7d, sub: 'on the way', tid: 'pulse-shipped' },
            ].map((x) => (
              <div key={x.tid} data-testid={x.tid} className="rounded-2xl bg-alp-dark/50 border border-alp-line/50 p-4 text-center">
                <x.icon size={15} strokeWidth={1.5} className="text-alp-gold mx-auto mb-2" />
                <div className="font-serif text-2xl text-alp-cream">{x.v}</div>
                <div className="text-[10px] tracking-[0.1em] uppercase text-alp-cream/50 mt-1">{x.label}</div>
                <div className="text-[10px] text-alp-cream/35">{x.sub}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
